import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
const darkTheme = {};
import { lightTheme } from "./themes";
import { Capacitor } from "@capacitor/core";
import CoilCalculator from "./CoilCalculator";
import OhmCalculator from './OhmCalculator';
import ohmIcon from './icons/ohm.svg';
import coilIcon from "./icons/coil.svg";
import { Share } from "@capacitor/share";
import { Plugins } from "@capacitor/core";
import translations from "./translations";
import CookieConsentOverlay from "./CookieConsentOverlay";
import "./AppStyles.css";
import VapingEducativo from "./VapingEducativo";
import PrivacyPolicy from "./PrivacyPolicy";
import calculatorIcon from "./icons/calculator_icon.svg";
import labIcon from "./icons/lab_icon.svg";
import mixseriesIcon from "./icons/mixseries_icon.svg";
import dilutionIcon from "./icons/dilution_icon.svg";
import recipesIcon from "./icons/recipes_icon.svg";
import vapingIcon from "./icons/vaping_icon.svg";
import settingIcon from "./icons/setting_icon.svg";
import headerLogo from "./icons/headerlogo.svg";
import menuLogo from "./icons/menulogo.svg";
import { LanguageProvider, LanguageContext } from "./LanguageContext"; // Importa il contesto

const McPuff = ({ defaultView }) => {
  const { language, setLanguage, t } = useContext(LanguageContext);
  console.log("MyComponent rendered, language:", language);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const getStyles = (currentTheme) => {
    currentTheme = currentTheme || lightTheme;
    const baseStyles = {
      container: {
        maxWidth: "600px",
        margin: "0 auto",
        fontFamily: '"Poppins", sans-serif',
        backgroundColor: "var(--background)",
        color: "var(--text)",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        transition: "background-color 0.3s ease",
        paddingTop: `${headerHeight + 20}px`, // Aggiunge spazio extra sopra il contenuto
        marginTop: "20px", // Aggiunge un po' di spazio tra l'header e il contenuto
      },
      header: {
        backgroundColor: "var(--headerBackground)",
        transition: "transform 0.3s ease-in-out",
        transform: isHeaderVisible ? "translateY(0)" : "translateY(-100%)",
        color: "var(--headerText)",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: `90px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 10px",
        zIndex: 1000,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)", // Aggiunge un'ombra sottile
      },
      headerLogo: {
        height: `75px`,
        maxHeight: `80px`,
        width: "auto",
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        top: "10px",
      },
      menuButton: {
        backgroundColor: "transparent",
        border: "none",
        color: "var(--headerText)",
        fontSize: "24px",
        cursor: "pointer",
        padding: "10px",
        zIndex: 1001,
        position: "relative", // Assicura che il pulsante sia sopra altri elementi
      },

      menuIcon: {
        fontSize: "24px",
        marginRight: "15px",
        color: "var(--background)",
        order: 0,
      },
      title: {
        margin: 0,
        fontSize: "24px",
        fontWeight: "600",
        color: "var(--background)",
      },
      themeToggle: {
        backgroundColor: "transparent",
        border: "none",
        color: "var(--background)",
        fontSize: "24px",
        cursor: "pointer",
        marginLeft: "auto",
        padding: "5px 10px",
        borderRadius: "5px",
        transition: "background-color 0.3s ease",
        order: 2,
      },
      content: {
        padding: "20px",
      },
      section: {
        marginBottom: "20px",
        backgroundColor: "var(--sectionBackground)",
        padding: "20px",
        borderRadius: "16px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
      },
      sectionTitle: {
        fontSize: "18px",
        fontWeight: "700",
        marginBottom: "15px",
        color: "var(--primary)",
      },
      inputGroup: {
        marginBottom: "15px",
      },
      label: {
        display: "block",
        marginBottom: "5px",
        fontWeight: "300",
        color: "var(--text)",
        fontSize: "14px",
      },
      inputWrapper: {
        display: "flex",
        alignItems: "center",
        backgroundColor: "var(--inputBackground)",
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #000000",
        maxWidth: "100%",
      },
      input: {
        flex: 1,
        padding: "10px 12px",
        border: "none",
        fontSize: "14px",
        outline: "none",
        backgroundColor: "transparent",
        color: "var(--primary)",
        minWidth: 0,
        fontWeight: "bold",
      },
      unit: {
        padding: "10px 12px",
        backgroundColor: "var(--unitBackground)",
        color: "var(--text)",
        fontWeight: "400",
        fontSize: "14px",
        whiteSpace: "nowrap",
      },
      sliderContainer: {
        width: "100%",
        marginTop: "10px",
        padding: "0 8px",
      },
      slider: {
        width: "calc(100% + 16px)",
        margin: "0 -8px",
        appearance: "none",
        height: "4px",
        borderRadius: "2px",
        background:
          "linear-gradient(to right, var(--primary), var(--secondary))",
        outline: "none",
        opacity: "0.7",
        transition: "opacity .2s",
      },
      sliderLabels: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "5px",
        fontSize: "12px",
        color: "var(--text)",
        margin: "0 -8px",
        position: "relative",
      },
      sliderLabel: {
        position: "absolute",
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
      },
      button: {
        width: "100%",
        padding: "12px",
        backgroundColor: "var(--calculateButton)",
        color: "var(--calculateButtonText)",
        border: "none",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "background-color 0.3s ease",
        boxShadow: "0 2px 4px var(--shadowColor)",
      },
      resultContainer: {
        marginTop: "20px",
        backgroundColor: "var(--sectionBackground)",
        borderRadius: "8px",
        padding: "15px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
      },
      resultSummary: {
        fontSize: "16px",
        fontWeight: "500",
        marginBottom: "10px",
        color: "var(--text)",
      },
      resultItem: {
        margin: "10px 0",
        fontSize: "14px",
        color: "var(--text)",
      },
      resultValue: {
        fontWeight: "500",
        color: "var(--primary)",
      },
      menu: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "20px",
        backgroundColor: "var(--background)",
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1001,
        overflowY: "auto", // Permette lo scorrimento se il contenuto è troppo lungo
      },
      menuItem: {
        display: "flex",
        alignItems: "center",
        padding: "15px 20px",
        marginBottom: "10px",
        backgroundColor: "var(--inputBackground)",
        borderRadius: "10px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        color: "var(--text)",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        width: "calc(100% - 40px)", // Sottrae il padding totale
        maxWidth: "300px", // Imposta una larghezza massima
        margin: "0 auto 10px auto", // Centra l'elemento e aggiunge margine sotto
      },
      menuItemIcon: {
        marginRight: "15px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--primary)",
        width: "24px",
        height: "24px",
      },
      menuItemText: {
        fontSize: "16px",
        fontWeight: "500",
      },
      modal: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      modalContent: {
        backgroundColor: "var(--background)",
        padding: "20px",
        borderRadius: "8px",
        width: "80%",
        maxWidth: "300px",
      },
      modalTitle: {
        fontSize: "18px",
        fontWeight: "500",
        marginBottom: "15px",
        color: "var(--primary)",
      },
      aromaComposition: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "15px",
      },
      compositionButton: {
        flex: 1,
        padding: "10px",
        border: "1px solid var(--border)",
        background: "var(--inputBackground)",
        cursor: "pointer",
      },
      activeButton: {
        backgroundColor: currentTheme.primary,
        color: "#FFFFFF",
        border: `1px solid ${currentTheme.primary}`,
      },
      modalButtons: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "15px",
      },
      cancelButton: {
        padding: "8px 12px",
        backgroundColor: "var(--inputBackground)",
        color: "var(--text)",
        border: "none",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "background-color 0.3s ease",
      },
      saveButton: {
        padding: "8px 12px",
        backgroundColor: "var(--primary)",
        color: "var(--background)",
        border: "none",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "background-color 0.3s ease",
      },
      select: {
        width: "100%",
        padding: "10px 12px",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        fontSize: "14px",
        backgroundColor: "var(--inputBackground)",
        color: "var(--text)",
        outline: "none",
      },
      aromaItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "var(--inputBackground)",
        padding: "8px 12px",
        borderRadius: "4px",
        marginBottom: "8px",
        fontSize: "14px",
      },
      removeButton: {
        padding: "4px 8px",
        backgroundColor: "var(--accent)",
        color: "var(--background)",
        border: "none",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
      },
      addButton: {
        padding: "8px 12px",
        backgroundColor: "var(--primary)",
        color: "var(--background)",
        border: "none",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "background-color 0.3s ease",
        marginBottom: "15px",
      },
      calculatorSelector: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "20px",
      },
      selectorButton: {
        flex: 1,
        padding: "10px",
        border: "none",
        background: currentTheme === darkTheme ? "#333333" : "#f0f0f0",
        color: currentTheme === darkTheme ? "#FFFFFF" : "#333333",
        cursor: "pointer",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.3s ease",
      },
      activeSelectorButton: {
        backgroundColor: currentTheme.primary,
        color: "#FFFFFF",
      },
      inactiveSelectorButton: {
        backgroundColor: currentTheme === darkTheme ? "#444444" : "#e0e0e0",
        color: currentTheme === darkTheme ? "#CCCCCC" : "#666666",
      },
      subSectionTitle: {
        fontSize: "16px",
        fontWeight: "600",
        marginBottom: "15px",
        color: "var(--primary)",
      },
      logo: {
        height: "120px", // Regola in base alle dimensioni del tuo logo
        transition: "opacity 0.3s ease",
        opacity: menuOpen ? 0 : 1,
      },
      menuLogo: {
        width: getMenuLogoSize(),
        height: "auto",
        marginTop: "1vh", // Usa una percentuale dell'altezza della viewport
        marginBottom: "1vh",
        alignSelf: "center",
      },
      shotMixContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        marginBottom: "20px",
      },
      nicotineButtonsContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        marginTop: "10px",
      },
      nicotineButton: {
        padding: "10px 15px",
        margin: "5px",
        borderRadius: "10px",
        border: "none",
        cursor: "pointer",
        transition: "all 0.3s ease",
        fontSize: "14px",
        fontWeight: "500",
        backgroundColor: theme === darkTheme ? "#333333" : "#E0E0E0",
        color: theme === darkTheme ? "#FFFFFF" : "#333333",
      },
      activeButton: {
        backgroundColor: theme.primary,
        color: "#FFFFFF",
      },
      tooltipContainer: {
        marginTop: "10px",
      },
      linkStyle: {
        color: currentTheme.primary,
        textDecoration: "underline",
        cursor: "pointer",
      },
      resultSummary: {
        fontSize: "16px",
        marginBottom: "10px",
      },
      inhalationTypeContainer: {
        display: "flex",
        gap: "10px",
        marginTop: "10px",
        flexDirection: "row",
      },
      inhalationTypeButton: {
        flex: 1,
        padding: "8px 12px",
        backgroundColor: "var(--inputBackground)",
        color: "var(--text)",
        border: "1px solid var(--border)",
        borderRadius: "4px",
        fontSize: "14px",
        cursor: "pointer",
      },
      resultList: {
        listStyle: "none",
        padding: 0,
      },
      tooltip: {
        position: "relative",
        display: "inline-block",
        marginLeft: "5px",
        cursor: "help",
      },
      tooltipText: {
        visibility: "visible",
        opacity: 1,
        backgroundColor: currentTheme.background,
        color: currentTheme.text,
        textAlign: "left",
        borderRadius: "6px",
        padding: "10px",
        position: "absolute",
        zIndex: 1000,
        top: "100%",
        left: "0",
        marginTop: "5px",
        boxShadow: `0 2px 4px ${currentTheme.shadowColor}`,
        fontSize: "12px",
        border: `1px solid ${currentTheme.border}`,
        width: "200px",
        maxWidth: "calc(100vw - 40px)",
      },
      tooltipIcon: {
        color: currentTheme.primary,
        fontSize: "16px",
      },
      "&:hover .tooltipText": {
        visibility: "visible",
        opacity: 1,
      },
      recipeDate: {
        fontSize: "12px",
        color: currentTheme.text,
        opacity: 0.7,
        marginBottom: "15px",
      },
      advancedDetails: {
        marginTop: "20px",
        padding: "15px",
        backgroundColor: "var(--sectionBackground)",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      },
      advancedTitle: {
        fontSize: "16px",
        fontWeight: "600",
        marginBottom: "10px",
        color: "var(--primary)",
      },
      advancedItem: {
        fontSize: "14px",
        margin: "5px 0",
        color: "var(--text)",
      },

      advancedInfo: {
        fontSize: "14px",
        marginTop: "15px",
        padding: "10px",
        backgroundColor: "var(--inputBackground)",
        borderRadius: "4px",
        border: "1px solid var(--border)",
        color: "var(--text)",
      },
      icon: {
        marginRight: "10px",
        color: "#4f9fca",
      },
      recipeItem: {
        backgroundColor: currentTheme.sectionBackground,
        borderRadius: "8px",
        padding: "15px",
        marginBottom: "20px",
        boxShadow: `0 2px 4px ${currentTheme.shadowColor}`,
      },
      chartContainer: {
        marginTop: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "350px",
        margin: "0 auto",
      },
      recipeNameInput: {
        width: "100%",
        padding: "8px",
        marginBottom: "10px",
        fontSize: "18px",
        fontWeight: "bold",
        border: `1px solid ${currentTheme.border}`,
        borderRadius: "4px",
        backgroundColor: currentTheme.inputBackground,
        color: currentTheme.text,
        "&::placeholder": {
          color: currentTheme.text,
          opacity: 0.7,
        },
      },
      deleteButton: {
        backgroundColor: currentTheme.accent,
        color: "#fff",
        border: "none",
        padding: "5px 10px",
        borderRadius: "4px",
        cursor: "pointer",
        marginTop: "10px",
      },
      exportButton: {
        width: "48%", // Cambiato da 60% a 48%
        maxWidth: "none", // Rimosso il maxWidth
        margin: "0", // Rimosso il margin
        padding: "12px",
        backgroundColor: currentTheme.primary,
        color: "#FFFFFF",
        border: "none",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "background-color 0.3s ease",
        boxShadow: `0 2px 4px ${currentTheme.shadowColor}`,
        textAlign: "center",
      },
      subTitle: {
        fontSize: "14px",
        fontWeight: "bold",
        marginTop: "15px",
        marginBottom: "5px",
        color: "var(--text)",
      },
      "@media (hover: hover)": {
        menuItem: {
          "&:hover": {
            backgroundColor: "var(--menuItemHover)",
            boxShadow: "0 4px 8px var(--menuItemShadow)",
            transform: "translateY(-2px)",
          },
        },
      },
      "@media (hover: hover)": {
        exportButton: {
          "&:hover": {
            backgroundColor: currentTheme.primaryHover || currentTheme.primary,
            transform: "translateY(-2px)",
            boxShadow: `0 4px 8px ${currentTheme.shadowColor}`,
          },
        },
      },
      "@media (hover: hover)": {
        selectorButton: {
          "&:hover": {
            opacity: 0.8,
            backgroundColor: currentTheme === darkTheme ? "#444444" : "#e0e0e0",
          },
        },
      },
      "@media (max-width: 768px)": {
        menu: {
          padding: "10px",
        },

        menuItem: {
          padding: "12px",
          fontSize: "14px",
        },
        inputWrapper: {
          flexDirection: "column",
        },
        input: {
          width: "100%",
        },
        unit: {
          alignSelf: "flex-start",
          marginTop: "5px",
        },
        inhalationTypeContainer: {
          justifyContent: "space-between",
        },
        inhalationTypeButton: {
          marginRight: "5px",
        },
        nicotineButtonsContainer: {
          justifyContent: "center",
        },
        nicotineButton: {
          margin: "5px",
        },
        body: {
          overflowY: "auto",
        },
      },
    };

    const mobileStyles = {
      tooltipText: {
        ...baseStyles.tooltipText,
        position: "absolute",
        left: "10px",
        right: "10px",
        top: "100%",
        transform: "none",
        width: "auto",
        maxWidth: "calc(100% - 20px)",
        fontSize: "12px",
        padding: "10px",
        zIndex: 1000,
        boxShadow: `0 2px 10px ${currentTheme.shadowColor}`,
      },
      resultTooltipText: {
        ...baseStyles.resultTooltipText,
        position: "absolute",
        left: "10px",
        right: "10px",
        top: "100%",
        transform: "none",
        width: "auto",
        maxWidth: "calc(100% - 20px)",
      },
    };

    return isMobile ? { ...baseStyles, ...mobileStyles } : baseStyles;
  };

  const getMenuLogoSize = () => {
    return window.innerWidth <= 768 ? "180px" : "150px";
  };

  const adjustTooltipPosition = (tooltipEl) => {
    if (tooltipEl) {
      const rect = tooltipEl.getBoundingClientRect();
      if (rect.left < 0) {
        tooltipEl.style.left = "0";
        tooltipEl.style.transform = "none";
      } else if (rect.right > window.innerWidth) {
        tooltipEl.style.left = "auto";
        tooltipEl.style.right = "0";
        tooltipEl.style.transform = "none";
      }
    }
  };

  // Stati
  const theme = lightTheme;
  const navigate = useNavigate();
  const [themeUpdate, setThemeUpdate] = useState(Date.now());
  const [activeView, setActiveView] = useState(defaultView || "calculator");
  const [menuOpen, setMenuOpen] = useState(false);
  const [liquidAmount, setLiquidAmount] = useState("");
  const [nicotineStrength, setNicotineStrength] = useState("");
  const [vgPgRatio, setVgPgRatio] = useState(70);
  const [baseStrength, setBaseStrength] = useState(20);
  const [nicotineComposition, setNicotineComposition] = useState("50/50");
  const [aromas, setAromas] = useState([]);
  const [aromaVgPg, setAromaVgPg] = useState("PG");
  const [result, setResult] = useState(null);
  const [showAromaModal, setShowAromaModal] = useState(false);
  const [newAromaPercentage, setNewAromaPercentage] = useState("");
  const [advancedMode, setAdvancedMode] = useState(false);
  const [aromaAmount, setAromaAmount] = useState("");
  const [dilutionPercentage, setDilutionPercentage] = useState("");
  const [dilutionResult, setDilutionResult] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [tooltipPosition, setTooltipPosition] = useState("top");

  const toggleMenu = () => {
    if (menuOpen) {
      // Se stiamo chiudendo il menu, prima facciamo scomparire il logo del menu
      setMenuOpen(false);
      setTimeout(() => {
        // Dopo un breve ritardo, facciamo riapparire il logo dell'header
        const headerLogo = document.querySelector("#headerLogo");
        if (headerLogo) headerLogo.style.opacity = "1";
      }, 300); // Questo dovrebbe corrispondere alla durata della transizione
    } else {
      // Se stiamo aprendo il menu, prima facciamo scomparire il logo dell'header
      const headerLogo = document.querySelector("#headerLogo");
      if (headerLogo) headerLogo.style.opacity = "0";
      setTimeout(() => {
        // Dopo un breve ritardo, apriamo il menu
        setMenuOpen(true);
      }, 300);
    }
  };

  // Stati aggiuntivi
  const [baseCalculatorInputs, setBaseCalculatorInputs] = useState({
    currentVolume: "",
    currentStrength: "",
    desiredStrength: "",
  });
  const [activeCalculator, setActiveCalculator] = useState("nicotine");
  const [baseCalculatorStrength, setBaseCalculatorStrength] = useState(20);
  const [mixBasesInputs, setMixBasesInputs] = useState({
    base1Volume: "",
    base1Strength: "",
    base2Volume: "",
    base2Strength: "",
  });
  const [base1Composition, setBase1Composition] = useState(70);
  const [base2Composition, setBase2Composition] = useState(70);
  const [liquidComposition, setLiquidComposition] = useState(70);
  const [shotMixInitialVolume, setShotMixInitialVolume] = useState("10");
  const [shotMixFinalVolume, setShotMixFinalVolume] = useState("20");
  const [desiredNicotine, setDesiredNicotine] = useState("3");
  const [availableNicotine, setAvailableNicotine] = useState(20);
  const [inhalationType, setInhalationType] = useState("MTL");
  const [nicotineBaseComposition, setNicotineBaseComposition] =
    useState("50/50");

  const tooltipRef = useRef(null);

  const [headerLogoVerticalPosition, setHeaderLogoVerticalPosition] =
    useState(0);

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const [showShareNotification, setShowShareNotification] = useState(false);

  const [headerLogoSize, setHeaderLogoSize] = useState(30);
  const [headerLogoPosition, setHeaderLogoPosition] = useState(0); // 0 per centro, negativo per sinistra, positivo per destra
  const [menuLogoSize, setMenuLogoSize] = useState(200);
  const [menuLogoPosition, setMenuLogoPosition] = useState(0);

  const [savedRecipes, setSavedRecipes] = useState([]);

  const [showSaveNotification, setShowSaveNotification] = useState(false);

  const { Permissions } = Plugins;
  const { SharePlugin } = Plugins;

  const [tooltipStates, setTooltipStates] = useState({
    dilution: false,
    result: false,
    resultInfo: false,
    mixInfo: false,
    nicotineInfo: false,
    aromaInfo: false,
    nicotineStrengthInfo: false,
    compositionInfo: false,
  });

  const toggleTooltip = (tooltipName) => {
    setTooltipStates((prev) => ({
      ...Object.fromEntries(Object.keys(prev).map((key) => [key, false])),
      [tooltipName]: !prev[tooltipName],
    }));
  };

  useEffect(() => {
    console.log("tooltipStates changed:", tooltipStates);
  }, [tooltipStates]);

  useEffect(() => {
    console.log("Result state updated:", result);
  }, [result]);

  useEffect(() => {
    applyTheme(theme);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const tooltips = document.querySelectorAll(".tooltip-container");
      tooltips.forEach((tooltip) => {
        const rect = tooltip.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          tooltip.style.top = "auto";
          tooltip.style.bottom = "100%";
        } else {
          tooltip.style.top = "100%";
          tooltip.style.bottom = "auto";
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Funzioni di utilità

  const handleScroll = () => {
    const currentScrollPos = window.pageYOffset;
    const isVisible = currentScrollPos < 50; // Puoi regolare questo valore
    setIsHeaderVisible(isVisible);
  };

  const [headerHeight, setHeaderHeight] = useState(60); // Valore iniziale, puoi modificarlo

  const adjustHeaderLogoSize = (newSize) => {
    setHeaderLogoSize(newSize);
    setHeaderHeight(Math.max(60, newSize + 20)); // Assicura che l'header sia sempre almeno 60px di altezza
  };

  const handleSetAvailableNicotine = (strength) => {
    setAvailableNicotine(strength);
    setThemeUpdate(Date.now());
  };

  const applyTheme = (currentTheme) => {
    const root = document.documentElement;
    Object.keys(currentTheme).forEach((key) => {
      root.style.setProperty(`--${key}`, currentTheme[key]);
    });
  };

  const resetResult = () => {
    setResult(null);
  };

const handleViewChange = (newView) => {
  setActiveView(newView);
  setMenuOpen(false);

  // Aggiungi questa logica di routing
  if (newView === "privacyPolicy") {
    navigate('/policyprivacy');
  } else if (newView === "vapingEducativo") {
    navigate('/vapingeducativo');
  } else {
    navigate('/');
  }

    // Resetta gli stati quando si cambia vista
    setResult(null);
    setLiquidAmount("");
    setNicotineStrength("");
    setVgPgRatio(70);
    setBaseStrength(20);
    setNicotineComposition("50/50");
    setAromas([]);
    setAromaVgPg("PG");

    // Resetta gli stati specifici del calcolatore shot/mix series
    setShotMixInitialVolume("10");
    setShotMixFinalVolume("20");
    setDesiredNicotine("3");
    setAvailableNicotine(20);
    setInhalationType("MTL");
  };

  const getValidDesiredNicotineRange = (
    initialVolume,
    finalVolume,
    availableNicotine
  ) => {
    const maxNicotine =
      (availableNicotine * (finalVolume - initialVolume)) / finalVolume;
    return {
      min: 0,
      max: Math.floor(maxNicotine),
    };
  };

  const getValidAvailableNicotineStrengths = (
    initialVolume,
    finalVolume,
    desiredNicotine
  ) => {
    const minNicotine = Math.ceil(
      (desiredNicotine * finalVolume) / (finalVolume - initialVolume)
    );
    return [6, 9, 12, 15, 18, 20].filter((strength) => strength >= minNicotine);
  };

  const getAvailableNicotineStrengths = (initialVolume, finalVolume) => {
    const ratio = finalVolume / initialVolume;
    if (ratio <= 2) return [6, 9, 12, 15, 18, 20];
    if (ratio <= 3) return [9, 12, 15, 18, 20];
    return [12, 15, 18, 20];
  };

  // Funzioni di calcolo
  const calculateMix = () => {
    const totalAmount = parseFloat(liquidAmount);
    const targetNic = parseFloat(nicotineStrength);
    const baseNic = parseFloat(baseStrength);
    const totalAromaPercentage = aromas.reduce(
      (sum, aroma) => sum + parseFloat(aroma.percentage),
      0
    );

    if (isNaN(totalAmount) || isNaN(targetNic) || isNaN(baseNic)) {
      alert("Per favore, inserisci tutti i valori numerici richiesti.");
      return;
    }

    const pgDensity = 1.036;
    const vgDensity = 1.261;

    const nicotineBaseVolume = (totalAmount * targetNic) / baseNic;

    let nicVg, nicPg;
    switch (nicotineComposition) {
      case "VG":
        nicVg = nicotineBaseVolume;
        nicPg = 0;
        break;
      case "PG":
        nicVg = 0;
        nicPg = nicotineBaseVolume;
        break;
      case "50/50":
        nicVg = nicotineBaseVolume * 0.5;
        nicPg = nicotineBaseVolume * 0.5;
        break;
      case "70/30":
        nicVg = nicotineBaseVolume * 0.7;
        nicPg = nicotineBaseVolume * 0.3;
        break;
      default:
        nicVg = nicotineBaseVolume * 0.5;
        nicPg = nicotineBaseVolume * 0.5;
    }

    const aromaAmount = (totalAromaPercentage / 100) * totalAmount;
    const aromaPg = aromaVgPg === "PG" ? aromaAmount : 0;
    const aromaVg = aromaVgPg === "VG" ? aromaAmount : 0;

    const totalVgNeeded = totalAmount * (vgPgRatio / 100);
    const totalPgNeeded = totalAmount - totalVgNeeded;

    const vgToAdd = Math.max(0, totalVgNeeded - nicVg - aromaVg);
    const pgToAdd = Math.max(0, totalPgNeeded - nicPg - aromaPg);
    const baseAmount = vgToAdd + pgToAdd;

    const baseVgGrams = (vgToAdd * vgDensity).toFixed(2);
    const basePgGrams = (pgToAdd * pgDensity).toFixed(2);
    const baseTotalGrams = (
      parseFloat(baseVgGrams) + parseFloat(basePgGrams)
    ).toFixed(2);

    const nicotineVgGrams = (nicVg * vgDensity).toFixed(2);
    const nicotinePgGrams = (nicPg * pgDensity).toFixed(2);
    const nicotineTotalGrams = (
      parseFloat(nicotineVgGrams) + parseFloat(nicotinePgGrams)
    ).toFixed(2);

    const aromaGrams = (
      aromaAmount * (aromaVgPg === "PG" ? pgDensity : vgDensity)
    ).toFixed(2);

    const totalVgGrams = (
      parseFloat(baseVgGrams) +
      parseFloat(nicotineVgGrams) +
      (aromaVgPg === "VG" ? parseFloat(aromaGrams) : 0)
    ).toFixed(2);
    const totalPgGrams = (
      parseFloat(basePgGrams) +
      parseFloat(nicotinePgGrams) +
      (aromaVgPg === "PG" ? parseFloat(aromaGrams) : 0)
    ).toFixed(2);
    const totalGrams = (
      parseFloat(totalVgGrams) + parseFloat(totalPgGrams)
    ).toFixed(2);

    // Calcolo delle note dinamiche
    let notes = [];
    const actualVgPercentage = (
      (parseFloat(totalVgGrams) / parseFloat(totalGrams)) *
      100
    ).toFixed(0);
    const actualPgPercentage = (100 - actualVgPercentage).toFixed(0);
    const desiredVgPgRatio = `${vgPgRatio}/${100 - vgPgRatio}`;
    const actualVgPgRatio = `${actualVgPercentage}/${actualPgPercentage}`;

    if (desiredVgPgRatio !== actualVgPgRatio) {
      notes.push(
        `La composizione finale ${actualVgPgRatio} differisce da quella desiderata ${desiredVgPgRatio} per compensare l'aroma ${aromaVgPg} e la base di nicotina ${nicotineComposition}.`
      );
    }

    if (aromaVgPg === "PG" && vgPgRatio > 50) {
      notes.push("L'aroma PG aumenta la quantità totale di PG nel mix finale.");
    } else if (aromaVgPg === "VG" && vgPgRatio < 50) {
      notes.push("L'aroma VG aumenta la quantità totale di VG nel mix finale.");
    }

    if (nicotineComposition !== "50/50") {
      notes.push(
        `La composizione ${nicotineComposition} della base di nicotina influenza il rapporto VG/PG finale.`
      );
    }

    setResult({
      summary: t("resultSummary", {
        totalAmount: totalAmount.toFixed(2),
        vgPgRatio: `${vgPgRatio}/${100 - vgPgRatio}`,
        targetNic: targetNic.toFixed(2),
        aromaPercentage:
          totalAromaPercentage > 0
            ? t("withAroma", { percentage: totalAromaPercentage.toFixed(2) })
            : "",
      }),
      baseAmount: baseAmount.toFixed(2),
      baseVg: vgToAdd.toFixed(2),
      basePg: pgToAdd.toFixed(2),
      nicotine: nicotineBaseVolume.toFixed(2),
      flaconi: Math.floor(nicotineBaseVolume / 10),
      flaconiResto: (nicotineBaseVolume % 10).toFixed(1),
      aroma: aromaAmount.toFixed(2),
      nicotineComposition: nicotineComposition,
      grams: {
        baseVg: baseVgGrams,
        basePg: basePgGrams,
        base: baseTotalGrams,
        nicotine: nicotineTotalGrams,
        aroma: aromaGrams,
        totalVg: totalVgGrams,
        totalPg: totalPgGrams,
        total: totalGrams,
      },
      notes: notes,
      actualVgPgRatio: actualVgPgRatio,
    });
  };

  const calculateDilution = () => {
    const aroma = parseFloat(aromaAmount);
    const percentage = parseFloat(dilutionPercentage);

    if (
      isNaN(aroma) ||
      isNaN(percentage) ||
      percentage <= 0 ||
      percentage > 100
    ) {
      alert("Per favore, inserisci valori validi.");
      return;
    }

    const totalAmount = (aroma * 100) / percentage;
    const baseToAdd = totalAmount - aroma;

    setDilutionResult({
      baseToAdd: baseToAdd.toFixed(2),
      aroma: aroma.toFixed(2),
      total: totalAmount.toFixed(2),
    });
  };

  const saveRecipe = () => {
    if (!result) return;

    const recipe = {
      id: Date.now(),
      name: "",
      ingredients: {
        baseAmount: result.baseAmount,
        baseVgPg: `${vgPgRatio}/${100 - vgPgRatio}`,
        nicotine: result.nicotine,
        nicotineStrength: `${baseStrength}mg`,
        nicotineFlaconi: result.flaconi,
        nicotineFlaconiResto: result.flaconiResto,
        nicotineComposition: nicotineComposition,
        aroma: result.aroma,
        finalVgPgRatio: `${vgPgRatio}/${100 - vgPgRatio}`,
        finalNicotineStrength: nicotineStrength,
      },
      createdAt: new Date().toLocaleString(),
    };

    const updatedRecipes = [...savedRecipes, recipe];
    setSavedRecipes(updatedRecipes);
    localStorage.setItem("savedRecipes", JSON.stringify(updatedRecipes));
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 3000);
  };

  const calculateBase = () => {
    const V1 = parseFloat(baseCalculatorInputs.currentVolume);
    const C1 = parseFloat(baseCalculatorInputs.currentStrength);
    const C2 = parseFloat(baseCalculatorInputs.desiredStrength);
    const C3 = baseCalculatorStrength;

    if (isNaN(V1) || isNaN(C1) || isNaN(C2) || C1 === C2) {
      alert(
        "Per favore, inserisci tutti i valori numerici richiesti e assicurati che la forza attuale e desiderata siano diverse."
      );
      return;
    }

    let V2, Vtot;

    if (C2 > C1) {
      // Aumentare la concentrazione
      V2 = (V1 * (C2 - C1)) / (C3 - C2);
      Vtot = V1 + V2;

      const flacons = Math.floor(V2 / 10);
      const extraMl = V2 % 10;
      const nicotineGrams =
        V2 *
        (nicotineComposition === "50/50"
          ? 1.1485
          : nicotineComposition === "VG"
          ? 1.261
          : 1.036);
      const totalGrams =
        Vtot *
        (liquidComposition === 50
          ? 1.1485
          : liquidComposition === 100
          ? 1.261
          : 1.036);

      setResult({
        baseCalculator: {
          message: t(
            C2 > C1 ? "increaseNicotineMessage" : "decreaseNicotineMessage",
            { strength: C2 }
          ),
          additive: t(C2 > C1 ? "addNicotineBase" : "addNeutralBase", {
            volume: V2.toFixed(2),
            composition: nicotineComposition,
            strength: C3,
            flacons: flacons,
            extraMl:
              extraMl > 0 ? t("extraMl", { amount: extraMl.toFixed(1) }) : "",
            grams: (C2 > C1 ? nicotineGrams : baseGrams).toFixed(2),
          }),
          finalVolume: t("finalVolumeN", {
            volume: Vtot.toFixed(2),
            grams: totalGrams.toFixed(2),
          }),
        },
      });
    } else {
      // Diminuire la concentrazione
      V2 = (V1 * (C1 - C2)) / C2;
      Vtot = V1 + V2;

      const formatVolume = (vol) =>
        Number.isInteger(vol) ? vol.toString() : vol.toFixed(2);
      const baseGrams =
        V2 *
        (liquidComposition === 50
          ? 1.1485
          : liquidComposition === 100
          ? 1.261
          : 1.036);
      const totalGrams =
        Vtot *
        (liquidComposition === 50
          ? 1.1485
          : liquidComposition === 100
          ? 1.261
          : 1.036);

      setResult({
        baseCalculator: {
          message: t("decreaseNicotineMessage", { strength: C2 }),
          additive: t("addNeutralBase", {
            volume: formatVolume(V2),
            composition: nicotineComposition,
            grams: baseGrams.toFixed(2),
          }),
          finalVolume: t("finalVolumeL", {
            volume: formatVolume(Vtot),
            grams: totalGrams.toFixed(2),
          }),
        },
      });
    }
  };

  const shareRecipe = async () => {
    if (!result) {
      alert(t("noResultToShare"));
      return;
    }

    const recipeDetails = `
    ${result.summary}
    ${t("neutralBase")} ${vgPgRatio}/${100 - vgPgRatio}: ${result.baseAmount} ml
    ${t("nicotineBase")} ${
      result.nicotineComposition
    } - ${baseStrength} mg/ml: ${result.nicotine} ml
    ${parseFloat(result.aroma) > 0 ? `${t("aromas")}: ${result.aroma} ml` : ""}
  `.trim();

    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: t("shareRecipeTitle"),
          text: recipeDetails,
          dialogTitle: t("shareDialogTitle"),
        });
      } else if (navigator.share) {
        await navigator.share({
          title: t("shareRecipeTitle"),
          text: recipeDetails,
        });
      } else {
        alert(t("webShareNotSupported"));
        return;
      }
      // Se arriviamo qui, la condivisione è avvenuta con successo
      alert(t("shareSuccess"));
    } catch (error) {
      console.error("Errore durante la condivisione:", error);
      if (error.message === "Share canceled") {
        // Non mostrare un alert se l'utente ha annullato la condivisione
        console.log("Condivisione annullata dall'utente");
      } else {
        alert(t("shareError"));
      }
    }
  };

  const calculateMixBases = () => {
    const { base1Volume, base1Strength, base2Volume, base2Strength } =
      mixBasesInputs;

    const base1Vg = parseFloat(base1Volume) * (base1Composition / 100);
    const base1Pg = parseFloat(base1Volume) - base1Vg;
    const base2Vg = parseFloat(base2Volume) * (base2Composition / 100);
    const base2Pg = parseFloat(base2Volume) - base2Vg;

    const totalVolume = parseFloat(base1Volume) + parseFloat(base2Volume);
    const totalVg = base1Vg + base2Vg;
    const totalPg = base1Pg + base2Pg;
    const finalVgPgRatio = ((totalVg / totalVolume) * 100).toFixed(0);

    const totalNicotine =
      parseFloat(base1Volume) * parseFloat(base1Strength) +
      parseFloat(base2Volume) * parseFloat(base2Strength);
    const finalStrength = totalNicotine / totalVolume;

    const pgDensity = 1.036;
    const vgDensity = 1.261;
    const totalGrams = (totalVg * vgDensity + totalPg * pgDensity).toFixed(2);

    setResult({
      baseCalculator: {
        message: t("mixBasesResult"),
        additive: t("totalVolumeD", {
          volume: totalVolume.toFixed(2),
          grams: totalGrams,
        }),
        finalVolume: t("finalCompositionD", {
          ratio: `${finalVgPgRatio}/${100 - finalVgPgRatio}`,
        }),
        finalStrength: t("finalStrengthD", {
          strength: finalStrength.toFixed(2),
        }),
      },
    });

    console.log("Calculated result before setting:", result);
    setResult(result);
    console.log("Result state after setting:", result);
  };

  const calculateShotMix = () => {
    const initialVolume = parseInt(shotMixInitialVolume);
    const finalVolume = parseInt(shotMixFinalVolume);
    const targetNico = parseFloat(desiredNicotine);
    const baseNico = parseFloat(availableNicotine);

    const nicotineVolume = Math.min(
      (targetNico / baseNico) * finalVolume,
      finalVolume - initialVolume
    );
    const aromaVolume = initialVolume;
    const neutralBaseVolume = finalVolume - aromaVolume - nicotineVolume;

    const neutralBaseComposition = inhalationType === "DL" ? "70/30" : "50/50";

    setResult({
      shotMixSeries: {
        totalVolume: finalVolume,
        aroma: aromaVolume,
        nicotineBase: Math.round(nicotineVolume * 10) / 10,
        nicotineStrength: baseNico,
        nicotineBaseComposition: nicotineBaseComposition,
        neutralBase: Math.round(neutralBaseVolume * 10) / 10,
        neutralBaseComposition,
      },
    });
  };

  // Componenti di rendering
  const renderAromaDilution = () => (
    <div style={getStyles(theme).content}>
      <div
        className="tooltip-container-dilution"
        style={{
          position: "relative",
          display: "inline-block",
          marginBottom: "20px",
        }}
      >
        <h2
          style={{
            ...getStyles(theme).sectionTitle,
            display: "inline-block",
            marginRight: "10px",
          }}
        >
          {t("calculateAromaDilution")}
        </h2>
        <span
          style={{
            ...getStyles(theme).tooltip,
            verticalAlign: "middle",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleTooltip("dilution");
          }}
        >
          <i
            className="fas fa-info-circle"
            style={getStyles(theme).tooltipIcon}
          ></i>
        </span>
        {tooltipStates.dilution && (
          <div
            style={{
              ...getStyles(theme).tooltipText,
              visibility: "visible",
              opacity: 1,
              position: "absolute",
              left: "10%",
              top: 40,
              transform: "translateX(10px)",
              width: "200px",
              zIndex: 1000,
              backgroundColor: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              padding: "10px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <p>{t("dilutionTooltip")}</p>
          </div>
        )}
      </div>
      <div style={getStyles(theme).inputGroup}>
        <label style={getStyles(theme).label}>{t("aromaAmount")}</label>
        <div style={getStyles(theme).inputWrapper}>
          <input
            style={getStyles(theme).input}
            type="number"
            value={aromaAmount}
            onChange={(e) => setAromaAmount(e.target.value)}
            placeholder={t("enterValue")}
          />
          <span style={getStyles(theme).unit}>ml</span>
        </div>
      </div>
      <div style={getStyles(theme).inputGroup}>
        <label style={getStyles(theme).label}>{t("dilutionPercentage")}</label>
        <div style={getStyles(theme).inputWrapper}>
          <input
            style={getStyles(theme).input}
            type="number"
            value={dilutionPercentage}
            onChange={(e) => setDilutionPercentage(e.target.value)}
            placeholder={t("enterValue")}
          />
          <span style={getStyles(theme).unit}>%</span>
        </div>
      </div>
      <button style={getStyles(theme).button} onClick={calculateDilution}>
        {t("calculate")}
      </button>
      {dilutionResult && (
        <div style={getStyles(theme).resultContainer}>
          <p style={getStyles(theme).resultSummary}>
            {t("dilutionResultSummary", {
              percentage: dilutionPercentage,
              total: dilutionResult.total,
              baseToAdd: dilutionResult.baseToAdd,
              aroma: dilutionResult.aroma,
            })}
          </p>
          <p style={getStyles(theme).resultValue}>
            <strong>
              {t("dilutionResultDetails", {
                total: dilutionResult.total,
                baseToAdd: dilutionResult.baseToAdd,
                aroma: dilutionResult.aroma,
              })}
            </strong>
          </p>
          <div
            className="tooltip-container"
            style={{
              position: "relative",
              display: "inline-block",
              marginLeft: "10px",
            }}
          >
            <span
              className="tooltip-icon-result"
              style={{
                ...getStyles(theme).tooltip,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleTooltip("result");
              }}
            >
              <i
                className="fas fa-info-circle"
                style={getStyles(theme).tooltipIcon}
              ></i>
            </span>
            {tooltipStates.result && (
              <div
                className="tooltip-result"
                style={{
                  ...getStyles(theme).tooltipText,
                  position: "fixed",
                  top: "80%",
                  left: "35%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: theme.background,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: "8px",
                  padding: "10px",
                  maxWidth: "80%",
                  width: "200px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  zIndex: 1000,
                  visibility: "visible",
                  opacity: 1,
                }}
              >
                <p>
                  <p>{t("resultTooltip")}</p>{" "}
                  <span
                    style={{
                      ...getStyles(theme).linkStyle,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewChange("calculator");
                      toggleTooltip("result");
                    }}
                  >
                    {t("calculator")}
                  </span>
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTooltip("result");
                  }}
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "none",
                    border: "none",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Funzione per esportare in PDF

  const renderCalculator = () => (
    <div style={getStyles(theme).content}>
      <div style={getStyles(theme).section}>
        <h2 style={getStyles(theme).sectionTitle}>{t("quantity")}</h2>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("finalQuantity")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={liquidAmount}
              onChange={(e) => setLiquidAmount(e.target.value)}
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("finalComposition")}</label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="50"
              max="80"
              step="10"
              value={vgPgRatio}
              onChange={(e) => setVgPgRatio(parseInt(e.target.value))}
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>50/50</span>
              <span>60/40</span>
              <span>70/30</span>
              <span>80/20</span>
            </div>
          </div>
        </div>

        <h2 style={getStyles(theme).sectionTitle}>
          {t("nicotine")}
          <span
            style={{
              ...getStyles(theme).tooltip,
              marginLeft: "5px",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleTooltip("nicotineInfo");
            }}
          >
            <i
              className="fas fa-info-circle"
              style={getStyles(theme).tooltipIcon}
            ></i>
          </span>
        </h2>
        {tooltipStates.nicotineInfo && (
          <div
            style={{
              ...getStyles(theme).tooltipText,
              visibility: "visible",
              opacity: 1,
              position: "absolute",
              left: "25%",
              top: 350,
              transform: "translateX(10px)",
              width: "200px",
              zIndex: 1000,
              backgroundColor: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              padding: "10px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <p>{t("nicotineTooltip")}</p>
          </div>
        )}

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("desiredNicotine")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={nicotineStrength}
              onChange={(e) => setNicotineStrength(e.target.value)}
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>mg</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("availableBaseStrength")}
          </label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="0"
              max="5"
              step="1"
              value={[6, 9, 12, 15, 18, 20].indexOf(baseStrength)}
              onChange={(e) =>
                setBaseStrength(
                  [6, 9, 12, 15, 18, 20][parseInt(e.target.value)]
                )
              }
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>6mg</span>
              <span>9mg</span>
              <span>12mg</span>
              <span>15mg</span>
              <span>18mg</span>
              <span>20mg</span>
            </div>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("nicotineComposition")}
          </label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={["VG", "PG", "50/50", "70/30"].indexOf(
                nicotineComposition
              )}
              onChange={(e) =>
                setNicotineComposition(
                  ["VG", "PG", "50/50", "70/30"][parseInt(e.target.value)]
                )
              }
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              {["VG", "PG", "50/50", "70/30"].map((label, index) => (
                <span
                  key={label}
                  style={{
                    ...getStyles(theme).sliderLabel,
                    left: `${(index / 3) * 100}%`,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={getStyles(theme).section}>
        <h2 style={getStyles(theme).sectionTitle}>
          {t("aromas")}
          <span
            style={{
              ...getStyles(theme).tooltip,
              marginLeft: "5px",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleTooltip("aromaInfo");
            }}
          >
            <i
              className="fas fa-info-circle"
              style={getStyles(theme).tooltipIcon}
            ></i>
          </span>
        </h2>
        {tooltipStates.aromaInfo && (
          <div
            style={{
              visibility: "visible",
              opacity: 1,
              position: "absolute",
              left: "22%",
              top: 690,
              transform: "translateX(10px)",
              width: "200px",
              zIndex: 1000,
              backgroundColor: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              padding: "10px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <p>{t("aromaTooltip")}</p>
          </div>
        )}
        {aromas.map((aroma, index) => (
          <div key={index} style={getStyles(theme).aromaItem}>
            <span>{aroma.percentage}%</span>
            <button
              onClick={() => removeAroma(index)}
              style={getStyles(theme).removeButton}
            >
              X
            </button>
          </div>
        ))}
        <button
          style={getStyles(theme).addButton}
          onClick={() => setShowAromaModal(true)}
        >
          {t("addAroma")}
        </button>
      </div>
      <button style={getStyles(theme).button} onClick={calculateMix}>
        {t("calculate")}
      </button>

      {result && (
        <div style={getStyles(theme).resultContainer}>
          <div style={getStyles(theme).calculatorSelector}>
            {/* Bottone Modalità Semplice */}
            <button
              style={{
                ...getStyles(theme).selectorButton,
                ...(!advancedMode
                  ? getStyles(theme).activeSelectorButton
                  : getStyles(theme).inactiveSelectorButton),
              }}
              onClick={() => setAdvancedMode(false)}
            >
              {t("simpleMode")}
            </button>

            {/* Bottone Modalità Avanzata */}
            <button
              style={{
                ...getStyles(theme).selectorButton,
                ...(advancedMode
                  ? getStyles(theme).activeSelectorButton
                  : getStyles(theme).inactiveSelectorButton),
              }}
              onClick={() => setAdvancedMode(true)}
            >
              {t("advancedMode")}
            </button>
          </div>

          {/* Riepilogo dei risultati */}
          <p style={getStyles(theme).resultSummary}>{result.summary}</p>

          {/* Dettagli dei risultati */}
          <div style={getStyles(theme).resultDetails}>
            <p style={getStyles(theme).resultItem}>
              <i className="fas fa-tint" style={getStyles(theme).icon}></i>
              {t("neutralBase")} {vgPgRatio}/{100 - vgPgRatio} {t("toUse")}:{" "}
              <span style={getStyles(theme).resultValue}>
                {result.baseAmount} ml ({result.grams.base} g)
              </span>
            </p>
            <p style={getStyles(theme).resultItem}>
              <i className="fas fa-flask" style={getStyles(theme).icon}></i>{" "}
              {t("nicotineBase")} {result.nicotineComposition} - {baseStrength}{" "}
              mg/ml:{" "}
              <span style={getStyles(theme).resultValue}>
                {result.nicotine} ml ({result.grams.nicotine} g) - (
                {result.flaconi} {t("bottles")} da 10ml
                {result.flaconiResto > 0
                  ? ` + 1 da ${
                      typeof result.flaconiResto === "number"
                        ? result.flaconiResto.toFixed(2)
                        : result.flaconiResto
                    }ml`
                  : ""}
                )
              </span>
            </p>

            {/* Risultato per Aroma */}
            {parseFloat(result.aroma) > 0 && (
              <p style={getStyles(theme).resultItem}>
                <i className="fas fa-vial" style={getStyles(theme).icon}></i>{" "}
                {t("aromas")}:{" "}
                <span style={getStyles(theme).resultValue}>
                  {result.aroma} ml ({result.grams.aroma} g)
                </span>
              </p>
            )}
          </div>
          {/* Tooltip per il risultato */}
          <div style={getStyles(theme).tooltipContainer}>
            <i
              className="fas fa-info-circle"
              style={{ ...getStyles(theme).tooltipIcon, cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleTooltip("resultInfo");
              }}
            ></i>
            {tooltipStates.resultInfo && (
              <div
                style={{
                  ...getStyles(theme).tooltipText,
                  visibility: "visible",
                  opacity: 1,
                  position: "fixed",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "280px",
                  maxWidth: "90vw",
                  padding: "10px",
                  zIndex: 1000,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                  backgroundColor: theme.background,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                }}
              >
                {t("resultInfo")}
              </div>
            )}
          </div>
          {/* Modalità Avanzata */}
          {advancedMode && (
            <div
              style={{ ...getStyles(theme).advancedDetails, padding: "20px" }}
            >
              <h4 style={getStyles(theme).advancedTitle}>
                {t("advancedDetails")}
              </h4>

              {/* Grafico a Torta */}
              <div
                style={{
                  ...getStyles(theme).chartContainer,
                  marginTop: "30px",
                }}
              >
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "VG",
                          value:
                            parseFloat(result.grams.baseVg) +
                            (aromaVgPg === "VG"
                              ? parseFloat(result.grams.aroma)
                              : 0),
                          mlValue:
                            parseFloat(result.baseVg) +
                            (aromaVgPg === "VG" ? parseFloat(result.aroma) : 0),
                        },
                        {
                          name: "PG",
                          value:
                            parseFloat(result.grams.basePg) +
                            (aromaVgPg === "PG"
                              ? parseFloat(result.grams.aroma)
                              : 0),
                          mlValue:
                            parseFloat(result.basePg) +
                            (aromaVgPg === "PG" ? parseFloat(result.aroma) : 0),
                        },
                        {
                          name: "Nicotina",
                          value: parseFloat(result.grams.nicotine),
                          mlValue: parseFloat(result.nicotine),
                        },
                        {
                          name: "Aroma",
                          value: parseFloat(result.grams.aroma),
                          mlValue: parseFloat(result.aroma),
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent, x, y, midAngle }) => {
                        if (percent < 0.08) return null;
                        const radius = 70;
                        const x1 =
                          x + radius * Math.cos((-midAngle * Math.PI) / 180);
                        const y1 =
                          y + radius * Math.sin((-midAngle * Math.PI) / 180);
                        return (
                          <text
                            x={x1}
                            y={y1}
                            fill="#fff"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize="10px"
                            fontWeight="bold"
                          >
                            {`${(percent * 100).toFixed(0)}%`}
                          </text>
                        );
                      }}
                      isAnimationActive={false}
                    >
                      {[
                        { name: "VG", color: "#ff6b6b" },
                        { name: "PG", color: "#4ecdc4" },
                        { name: "Nicotina", color: "#45b7d1" },
                        { name: "Aroma", color: "#f7b731" },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => {
                        const mlValue = props.payload.mlValue;
                        return [
                          `${mlValue.toFixed(2)}ml, ${value.toFixed(2)}g`,
                          name,
                        ];
                      }}
                      isAnimationActive={false}
                    />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <h5 style={getStyles(theme).subTitle}>{t("baseComposition")}</h5>
              <p style={getStyles(theme).advancedItem}>
                {t("baseVG")}: {result.baseVg} ml ({result.grams.baseVg} g)
              </p>
              <p style={getStyles(theme).advancedItem}>
                {t("basePG")}: {result.basePg} ml ({result.grams.basePg} g)
              </p>

              <h5 style={getStyles(theme).subTitle}>
                {t("nicotineComposition")}
              </h5>
              <p style={getStyles(theme).advancedItem}>
                {t("nicotineVG")}:{" "}
                {(
                  result.nicotine *
                  (nicotineComposition === "VG"
                    ? 1
                    : nicotineComposition === "50/50"
                    ? 0.5
                    : nicotineComposition === "70/30"
                    ? 0.7
                    : 0)
                ).toFixed(2)}{" "}
                ml (
                {(
                  result.grams.nicotine *
                  (nicotineComposition === "VG"
                    ? 1
                    : nicotineComposition === "50/50"
                    ? 0.5
                    : nicotineComposition === "70/30"
                    ? 0.7
                    : 0)
                ).toFixed(2)}{" "}
                g)
              </p>
              <p style={getStyles(theme).advancedItem}>
                {t("nicotinePG")}:{" "}
                {(
                  result.nicotine *
                  (nicotineComposition === "PG"
                    ? 1
                    : nicotineComposition === "50/50"
                    ? 0.5
                    : nicotineComposition === "70/30"
                    ? 0.3
                    : 0)
                ).toFixed(2)}{" "}
                ml (
                {(
                  result.grams.nicotine *
                  (nicotineComposition === "PG"
                    ? 1
                    : nicotineComposition === "50/50"
                    ? 0.5
                    : nicotineComposition === "70/30"
                    ? 0.3
                    : 0)
                ).toFixed(2)}{" "}
                g)
              </p>

              <h5 style={getStyles(theme).subTitle}>{t("aromaComposition")}</h5>
              <p style={getStyles(theme).advancedItem}>
                {aromaVgPg} {t("ofAroma")}: {result.aroma} ml (
                {result.grams.aroma} g)
              </p>

              <h5 style={getStyles(theme).subTitle}>{t("totals")}</h5>
              <p style={getStyles(theme).advancedItem}>
                {t("totalVG")}: {result.grams.totalVg} g
              </p>
              <p style={getStyles(theme).advancedItem}>
                {t("totalPG")}: {result.grams.totalPg} g
              </p>
              <p style={getStyles(theme).advancedItem}>
                {t("totalOverall")}: {result.grams.total} g
              </p>

              <h5 style={getStyles(theme).subTitle}>
                {t("finalComposition")}
                <span
                  className="tooltip-container"
                  style={{
                    ...getStyles(theme).tooltip,
                    marginLeft: "5px",
                    position: "relative",
                    display: "inline-block",
                    cursor: "pointer",
                  }}
                >
                  <i
                    className="fas fa-info-circle"
                    style={getStyles(theme).tooltipIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleTooltip("compositionInfo");
                    }}
                  ></i>
                  {tooltipStates.compositionInfo && (
                    <div
                      ref={tooltipRef}
                      style={{
                        position: "absolute",
                        left: "-100px",
                        bottom: "100%",
                        width: "200px",
                        padding: "10px",
                        marginBottom: "10px",
                        zIndex: 1000,
                        backgroundColor: theme.background,
                        color: theme.text,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        fontSize: "12px",
                        lineHeight: "1.4",
                        whiteSpace: "normal",
                        textAlign: "left",
                      }}
                    >
                      {t("compositionTooltip")}
                    </div>
                  )}
                </span>
              </h5>
              <p style={getStyles(theme).advancedItem}>
                {t("actualFinalComposition")}: {result.actualVgPgRatio} VG/PG
              </p>
            </div>
          )}
          {/* Bottoni per Salva Ricetta ed Esporta PDF */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            <button onClick={saveRecipe} style={getStyles(theme).exportButton}>
              {t("saveRecipe")}
            </button>
            <button
              onClick={shareRecipe}
              style={getStyles(theme).exportButton}
              disabled={!result}
            >
              {t("shareRecipe")}
            </button>
          </div>
          {showSaveNotification && (
            <div
              style={{
                position: "fixed",
                bottom: "80px",
                right: "230px",
                backgroundColor: theme.background,
                color: theme.text,
                padding: "8px 12px",
                borderRadius: "4px",
                boxShadow: `0 2px 4px ${theme.shadowColor}`,
                zIndex: 1000,
                fontSize: "12px",
                border: `1px solid ${theme.border}`,
              }}
            >
              {t("recipeSaved")}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderBaseCalculator = () => {
    const renderNicotineCalculator = () => (
      <div style={getStyles(theme).section}>
        <h3 style={getStyles(theme).subSectionTitle}>
          {t("increaseReduceStrength")}
        </h3>

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("currentBase")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={baseCalculatorInputs.currentVolume}
              onChange={(e) =>
                setBaseCalculatorInputs({
                  ...baseCalculatorInputs,
                  currentVolume: e.target.value,
                })
              }
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("liquidComposition")}</label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="50"
              max="80"
              step="10"
              value={liquidComposition}
              onChange={(e) => setLiquidComposition(parseInt(e.target.value))}
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>50/50</span>
              <span>60/40</span>
              <span>70/30</span>
              <span>80/20</span>
            </div>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("currentNicotineStrength")}
          </label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={baseCalculatorInputs.currentStrength}
              onChange={(e) =>
                setBaseCalculatorInputs({
                  ...baseCalculatorInputs,
                  currentStrength: e.target.value,
                })
              }
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>mg/ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("desiredNicotineStrength")}
          </label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={baseCalculatorInputs.desiredStrength}
              onChange={(e) =>
                setBaseCalculatorInputs({
                  ...baseCalculatorInputs,
                  desiredStrength: e.target.value,
                })
              }
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>mg/ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("availableBaseStrength")}
            <span
              style={{
                ...getStyles(theme).tooltip,
                marginLeft: "5px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleTooltip("nicotineStrengthInfo");
              }}
            >
              <i
                className="fas fa-info-circle"
                style={getStyles(theme).tooltipIcon}
              ></i>
            </span>
          </label>
          {tooltipStates.nicotineStrengthInfo && (
            <div
              style={{
                ...getStyles(theme).tooltipText,
                visibility: "visible",
                opacity: 1,
                position: "absolute",
                left: "70%",
                top: "60%",
                transform: "translateX(-50%)",
                width: "200px",
                zIndex: 1000,
              }}
            >
              <p>{t("nicotineStrengthInfo")}</p>
            </div>
          )}
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="0"
              max="5"
              step="1"
              value={[6, 9, 12, 15, 18, 20].indexOf(baseCalculatorStrength)}
              onChange={(e) =>
                setBaseCalculatorStrength(
                  [6, 9, 12, 15, 18, 20][parseInt(e.target.value)]
                )
              }
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>6mg</span>
              <span>9mg</span>
              <span>12mg</span>
              <span>15mg</span>
              <span>18mg</span>
              <span>20mg</span>
            </div>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("nicotineComposition")}
          </label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={["VG", "PG", "50/50", "70/30"].indexOf(
                nicotineComposition
              )}
              onChange={(e) =>
                setNicotineComposition(
                  ["VG", "PG", "50/50", "70/30"][parseInt(e.target.value)]
                )
              }
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              {["VG", "PG", "50/50", "70/30"].map((label, index) => (
                <span
                  key={label}
                  style={{
                    ...getStyles(theme).sliderLabel,
                    left: `${(index / 3) * 100}%`,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div style={getStyles(theme).buttonContainer}>
          <button
            style={{
              ...getStyles(theme).button,
              marginTop: "20px", // Aggiungi questa riga per abbassare il bottone
            }}
            onClick={calculateBase}
          >
            {t("calculateBase")}
          </button>
        </div>
      </div>
    );

    const renderMixBasesCalculator = () => (
      <div style={{ ...getStyles(theme).section, position: "relative" }}>
        <h3 style={getStyles(theme).subSectionTitle}>
          {t("createMixLiquids")}
          <i
            className="fas fa-info-circle"
            style={{
              ...getStyles(theme).tooltipIcon,
              marginLeft: "10px",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleTooltip("mixInfo");
            }}
          ></i>
        </h3>
        {tooltipStates.mixInfo && (
          <div
            style={{
              ...getStyles(theme).tooltipText,
              visibility: "visible",
              opacity: 1,
              position: "fixed",
              left: "50%",
              top: "20%",
              transform: "translate(-50%, -50%)",
              width: "280px",
              maxWidth: "90vw",
              padding: "10px",
              zIndex: 1000,
              boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
              borderRadius: "4px",
              backgroundColor: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
            }}
          >
            <p>{t("mixTooltip")}</p>
          </div>
        )}

        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("base1")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={mixBasesInputs.base1Volume}
              onChange={(e) =>
                setMixBasesInputs({
                  ...mixBasesInputs,
                  base1Volume: e.target.value,
                })
              }
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("baseStrength")} 1</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={mixBasesInputs.base1Strength}
              onChange={(e) =>
                setMixBasesInputs({
                  ...mixBasesInputs,
                  base1Strength: e.target.value,
                })
              }
              placeholder={t("strength")}
            />
            <span style={getStyles(theme).unit}>mg/ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("baseComposition")} 1</label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="50"
              max="80"
              step="10"
              value={base1Composition}
              onChange={(e) => setBase1Composition(parseInt(e.target.value))}
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>50/50</span>
              <span>60/40</span>
              <span>70/30</span>
              <span>80/20</span>
            </div>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("base2")}</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={mixBasesInputs.base2Volume}
              onChange={(e) =>
                setMixBasesInputs({
                  ...mixBasesInputs,
                  base2Volume: e.target.value,
                })
              }
              placeholder={t("enterValue")}
            />
            <span style={getStyles(theme).unit}>ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("baseStrength")} 2</label>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={mixBasesInputs.base2Strength}
              onChange={(e) =>
                setMixBasesInputs({
                  ...mixBasesInputs,
                  base2Strength: e.target.value,
                })
              }
              placeholder={t("strength")}
            />
            <span style={getStyles(theme).unit}>mg/ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("baseComposition")} 2</label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="50"
              max="80"
              step="10"
              value={base2Composition}
              onChange={(e) => setBase2Composition(parseInt(e.target.value))}
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>50/50</span>
              <span>60/40</span>
              <span>70/30</span>
              <span>80/20</span>
            </div>
          </div>
        </div>
        <button style={getStyles(theme).button} onClick={calculateMixBases}>
          {t("calculateMix")}
        </button>
      </div>
    );

    return (
      <div style={getStyles(theme).content}>
        <div style={getStyles(theme).calculatorSelector}>
          <button
            style={{
              ...getStyles(theme).selectorButton,
              ...(activeCalculator === "nicotine"
                ? getStyles(theme).activeButton
                : {}),
            }}
            onClick={() => setActiveCalculator("nicotine")}
          >
            {t("adjustNicotine")}
          </button>
          <button
            style={{
              ...getStyles(theme).selectorButton,
              ...(activeCalculator === "mixBases"
                ? getStyles(theme).activeButton
                : {}),
            }}
            onClick={() => {
              setActiveCalculator("mixBases");
              setResult(null);
            }}
          >
            {t("mixYourBases")}
          </button>
        </div>
        {activeCalculator === "nicotine"
          ? renderNicotineCalculator()
          : renderMixBasesCalculator()}
        {result && result.baseCalculator && (
          <div style={getStyles(theme).resultContainer}>
            <p style={getStyles(theme).resultItem}>
              {result.baseCalculator.message}
            </p>
            <p style={getStyles(theme).resultValue}>
              <strong>{result.baseCalculator.additive}</strong>
            </p>
            <p style={getStyles(theme).resultItem}>
              {result.baseCalculator.finalVolume}
            </p>
            {result.baseCalculator.finalStrength && (
              <p style={getStyles(theme).resultItem}>
                {result.baseCalculator.finalStrength}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderShotMixSeries = () => {
    const initialVolumeOptions = [
      { value: "10", label: "10ml" },
      { value: "20", label: "20ml" },
      { value: "30", label: "30ml" },
      { value: "40", label: "40ml" },
      { value: "50", label: "50ml" },
      { value: "60", label: "60ml" },
    ];

    const renderNicotineButtons = () => (
      <div style={getStyles(theme).nicotineButtonsContainer}>
        {validAvailableNicotineStrengths.map((strength) => (
          <button
            key={strength}
            style={{
              ...getStyles(theme).nicotineButton,
              ...(availableNicotine === strength
                ? getStyles(theme).activeButton
                : {}),
            }}
            onClick={() => setAvailableNicotine(strength)}
          >
            {strength}mg
          </button>
        ))}
      </div>
    );

    const getFinalVolumeOptions = () => {
      const initialVolume = parseInt(shotMixInitialVolume);
      const options = [];
      for (let i = initialVolume + 10; i <= 100; i += 10) {
        options.push({ value: i.toString(), label: `${i}ml` });
      }
      return options;
    };

    const initialVolume = parseInt(shotMixInitialVolume);
    const finalVolume = parseInt(shotMixFinalVolume);

    const validDesiredNicotineRange = getValidDesiredNicotineRange(
      initialVolume,
      finalVolume,
      availableNicotine
    );
    const validAvailableNicotineStrengths = getValidAvailableNicotineStrengths(
      initialVolume,
      finalVolume,
      parseInt(desiredNicotine)
    );

    return (
      <div style={getStyles(theme).content}>
        <h2 style={getStyles(theme).sectionTitle}>
          {t("composeShotMixSeries")}
        </h2>
        <div style={getStyles(theme).shotMixContainer}>
          <p>{t("shotMixFrom")}</p>
          <select
            style={getStyles(theme).select}
            value={shotMixInitialVolume}
            onChange={(e) => {
              setShotMixInitialVolume(e.target.value);
              setShotMixFinalVolume((parseInt(e.target.value) + 10).toString());
            }}
          >
            {initialVolumeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p>{t("readyEliquidFrom")}</p>
          <select
            style={getStyles(theme).select}
            value={shotMixFinalVolume}
            onChange={(e) => setShotMixFinalVolume(e.target.value)}
          >
            {getFinalVolumeOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("desiredNicotine")}</label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min={validDesiredNicotineRange.min}
              max={validDesiredNicotineRange.max}
              step="1"
              value={desiredNicotine}
              onChange={(e) => setDesiredNicotine(e.target.value)}
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              <span>{validDesiredNicotineRange.min}mg</span>
              <span>{Math.floor(validDesiredNicotineRange.max / 2)}mg</span>
              <span>{validDesiredNicotineRange.max}mg</span>
            </div>
          </div>
          <div style={getStyles(theme).inputWrapper}>
            <input
              style={getStyles(theme).input}
              type="number"
              value={desiredNicotine}
              onChange={(e) => {
                const value = Math.min(
                  Math.max(
                    parseInt(e.target.value) || 0,
                    validDesiredNicotineRange.min
                  ),
                  validDesiredNicotineRange.max
                );
                setDesiredNicotine(value.toString());
              }}
              min={validDesiredNicotineRange.min}
              max={validDesiredNicotineRange.max}
            />
            <span style={getStyles(theme).unit}>mg/ml</span>
          </div>
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>{t("availableNicotine")}</label>
          {renderNicotineButtons()}
        </div>
        <div style={getStyles(theme).inputGroup}>
          <label style={getStyles(theme).label}>
            {t("nicotineBaseComposition")}
          </label>
          <div style={getStyles(theme).sliderContainer}>
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={["VG", "PG", "50/50", "70/30"].indexOf(
                nicotineBaseComposition
              )}
              onChange={(e) =>
                setNicotineBaseComposition(
                  ["VG", "PG", "50/50", "70/30"][parseInt(e.target.value)]
                )
              }
              style={getStyles(theme).slider}
            />
            <div style={getStyles(theme).sliderLabels}>
              {["VG", "PG", "50/50", "70/30"].map((label, index) => (
                <span
                  key={label}
                  style={{
                    ...getStyles(theme).sliderLabel,
                    left:
                      index === 3
                        ? "calc(100% - 30px)"
                        : `${(index / 3) * 100}%`,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ ...getStyles(theme).inputGroup, marginTop: "30px" }}>
          <label style={getStyles(theme).label}>{t("inhalationType")}</label>
          <div style={getStyles(theme).inhalationTypeContainer}>
            <button
              style={{
                ...getStyles(theme).inhalationTypeButton,
                ...(inhalationType === "MTL"
                  ? getStyles(theme).activeButton
                  : {}),
              }}
              onClick={() => setInhalationType("MTL")}
            >
              {t("mtl")}
            </button>
            <button
              style={{
                ...getStyles(theme).inhalationTypeButton,
                ...(inhalationType === "DL"
                  ? getStyles(theme).activeButton
                  : {}),
              }}
              onClick={() => setInhalationType("DL")}
            >
              {t("dl")}
            </button>
          </div>
        </div>
        <button style={getStyles(theme).button} onClick={calculateShotMix}>
          {t("calculate")}
        </button>
        {result && result.shotMixSeries && (
          <div style={getStyles(theme).resultContainer}>
            <p style={getStyles(theme).resultSummary}>
              {t("shotMixResult", {
                finalVolume: result.shotMixSeries.totalVolume,
                desiredNicotine: desiredNicotine,
              })
                .replace(/{finalVolume}/g, result.shotMixSeries.totalVolume)
                .replace(/{desiredNicotine}/g, desiredNicotine)}
            </p>
            <ul style={getStyles(theme).resultList}>
              <li style={getStyles(theme).resultItem}>
                {t("yourAroma")}:{" "}
                <span style={getStyles(theme).resultValue}>
                  {result.shotMixSeries.aroma} ml
                </span>
              </li>
              <li style={getStyles(theme).resultItem}>
                {t("nicotineBase")} {result.shotMixSeries.nicotineStrength}
                mg/ml:{" "}
                <span style={getStyles(theme).resultValue}>
                  {result.shotMixSeries.nicotineBase} ml
                </span>
              </li>
              {result.shotMixSeries.neutralBase > 0 && (
                <li style={getStyles(theme).resultItem}>
                  {t("neutralBase")}{" "}
                  {result.shotMixSeries.neutralBaseComposition}:{" "}
                  <span style={getStyles(theme).resultValue}>
                    {result.shotMixSeries.neutralBase} ml
                  </span>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderRecipes = () => (
    <div style={getStyles(theme).content}>
      <h2 style={getStyles(theme).sectionTitle}>{t("savedRecipes")}</h2>
      {!savedRecipes || savedRecipes.length === 0 ? (
        <p>{t("noSavedRecipes")}</p>
      ) : (
        savedRecipes.map((recipe) => (
          <div key={recipe.id} style={getStyles(theme).recipeItem}>
            <input
              type="text"
              value={recipe.name || ""}
              onChange={(e) => updateRecipeName(recipe.id, e.target.value)}
              style={getStyles(theme).recipeNameInput}
              placeholder={t("nameYourRecipe")}
            />
            <p style={getStyles(theme).recipeDate}>
              {t("createdOn")}: {recipe.createdAt}
            </p>
            <p>
              {t("neutralBase")} {recipe.ingredients?.baseVgPg || ""}:{" "}
              {recipe.ingredients?.baseAmount || ""} ml
            </p>
            <p>
              {t("nicotine")} {recipe.ingredients?.nicotineStrength || ""}{" "}
              {recipe.ingredients?.nicotineComposition || ""}:{" "}
              {recipe.ingredients?.nicotine || ""} ml (
              {recipe.ingredients?.nicotineFlaconi || ""} {t("bottles")}{" "}
              {t("from")} 10ml
              {recipe.ingredients?.nicotineFlaconiResto > 0
                ? ` + 1 ${t("from")} ${
                    recipe.ingredients.nicotineFlaconiResto
                  }ml`
                : ""}
              )
            </p>
            <p>
              {t("aroma")}: {recipe.ingredients?.aroma || ""} ml
            </p>
            <p>
              {t("finalComposition")}:{" "}
              {recipe.ingredients?.finalVgPgRatio || ""} VG/PG
            </p>
            <p>
              {t("finalNicotineStrength")}:{" "}
              {recipe.ingredients?.finalNicotineStrength || ""} mg/ml
            </p>
            <button
              onClick={() => deleteRecipe(recipe.id)}
              style={getStyles(theme).deleteButton}
            >
              {t("delete")}
            </button>
          </div>
        ))
      )}
    </div>
  );

  const renderSettings = () => (
    <div style={getStyles(theme).content}>
      <div style={getStyles(theme).section}>
        <h2 style={getStyles(theme).sectionTitle}>{t("language")}</h2>
        <select
          style={getStyles(theme).select}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <option value="it">Italiano</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="fr">Français</option>
          <option value="de">Deutsch</option>
          <option value="pt">Português</option>
          <option value="zh">中文(简体)</option>
          <option value="zh-TW">中文(繁體)</option>
          <option value="ru">Русский</option>
          <option value="ko">한국어</option>
        </select>
      </div>
      <div style={getStyles(theme).section}>
        <h2 style={getStyles(theme).sectionTitle}>{t("privacyPolicy")}</h2>
        <button
          style={getStyles(theme).button}
          onClick={() => setActiveView("privacyPolicy")}
        >
          {t("viewPrivacyPolicy")}
        </button>
      </div>
    </div>
  );

  const updateRecipeName = (id, newName) => {
    const updatedRecipes = savedRecipes.map((recipe) =>
      recipe.id === id ? { ...recipe, name: newName } : recipe
    );
    setSavedRecipes(updatedRecipes);
    localStorage.setItem("savedRecipes", JSON.stringify(updatedRecipes));
  };

  const deleteRecipe = (id) => {
    const updatedRecipes = savedRecipes.filter((recipe) => recipe.id !== id);
    setSavedRecipes(updatedRecipes);
    localStorage.setItem("savedRecipes", JSON.stringify(updatedRecipes));
  };

  const renderMenu = () => {
    const menuItems = [
      {
        icon: (
          <img
            src={calculatorIcon}
            alt="Calculator"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("calculator"),
        view: "calculator",
      },
      {
        icon: (
          <img src={labIcon} alt="Lab" style={getStyles(theme).menuItemIcon} />
        ),
        text: t("baseCalculator"),
        view: "baseCalculator",
      },
      {
        icon: (
          <img
            src={mixseriesIcon}
            alt="Mix Series"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("shotMixSeries"),
        view: "shotMixSeries",
      },
      {
        icon: (
          <img
            src={dilutionIcon}
            alt="Dilution"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("aromaDilution"),
        view: "aromaDilution",
      },
{
        icon: (
          <img
            src={coilIcon}
            alt="Coil Calculator"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("coilCalculator"),
        view: "coilCalculator",
      },
      {
        icon: (
          <img
            src={ohmIcon}
            alt="Ohm Calculator"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("ohmCalculator"),
        view: "ohmCalculator",
      },
      {
        icon: (
          <img
            src={recipesIcon}
            alt="Recipes"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("recipes"),
        view: "recipes",
      },
      {
        icon: (
          <img
            src={vapingIcon}
            alt="Vaping Educativo"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("vapingEducativo"),
        view: "vapingEducativo",
      },
      {
        icon: (
          <img
            src={settingIcon}
            alt="Settings"
            style={getStyles(theme).menuItemIcon}
          />
        ),
        text: t("settings"),
        view: "settings",
      },
    ];

    return (
      <div style={getStyles(theme).menu}>
        <img
          src={require("./icons/menulogo.svg").default}
          alt="Vape Formula Pro"
          style={getStyles(theme).menuLogo}
        />
        {menuItems.map((item, index) => (
          <div
            key={index}
            style={getStyles(theme).menuItem}
            onClick={() => handleViewChange(item.view)}
          >
            <span style={getStyles(theme).menuItemIcon}>{item.icon}</span>
            <span style={getStyles(theme).menuItemText}>{item.text}</span>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    // Questo effetto si attiva quando la lingua cambia
    // Puoi aggiungere qui logica aggiuntiva se necessario
  }, [language]);

  // Effetti collaterali
  useEffect(() => {
    const loadedRecipes = localStorage.getItem("savedRecipes");
    if (loadedRecipes) {
      setSavedRecipes(JSON.parse(loadedRecipes));
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
      setThemeUpdate(Date.now());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".tooltip-container-dilution") &&
        !event.target.closest(".tooltip-container-result")
      ) {
        setTooltipStates({ dilution: false, result: false });
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".tooltip-container")) {
        setTooltipStates((prev) =>
          Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
        );
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (tooltipStates.result) {
      const tooltipElement = document.querySelector(".tooltip-result");
      const iconElement = document.querySelector(".tooltip-icon-result");
      if (tooltipElement && iconElement) {
        const rect = iconElement.getBoundingClientRect();
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        setTooltipPosition(spaceAbove > spaceBelow ? "top" : "bottom");
      }
    }
  }, [tooltipStates.result]);

  // Funzioni per la gestione degli aromi
  const addAroma = () => {
    if (newAromaPercentage) {
      setAromas([...aromas, { percentage: newAromaPercentage }]);
      setNewAromaPercentage("");
      setShowAromaModal(false);
    }
  };

  const removeAroma = (index) => {
    const newAromas = [...aromas];
    newAromas.splice(index, 1);
    setAromas(newAromas);
  };

  // Renderizzazione principale del componente
  return (
    <div style={getStyles(theme).container}>
      <header style={getStyles(theme).header}>
        <button style={getStyles(theme).menuButton} onClick={toggleMenu}>
          {menuOpen ? "✕" : "☰"}
        </button>
        <img
          src={require("./icons/headerlogo.svg").default}
          alt="Vape Formula Pro"
          style={getStyles(theme).headerLogo}
        />
      </header>
      {menuOpen && (
        <div style={getStyles(theme).menu}>
          <button
            style={{
              ...getStyles(theme).menuButton,
              position: "absolute",
              top: "10px",
              left: "10px",
              color: theme.text,
              backgroundColor: "transparent",
              fontSize: "24px",
              zIndex: 1002,
            }}
            onClick={toggleMenu}
          >
            ✕
          </button>
          <img
            src={require("./icons/menulogo.svg").default}
            alt="Vape Formula Pro"
            style={getStyles(theme).menuLogo}
          />

          {renderMenu()}
        </div>
      )}
      <>
        {activeView === "calculator" && renderCalculator()}
        {activeView === "baseCalculator" && renderBaseCalculator()}
        {activeView === "shotMixSeries" && renderShotMixSeries()}
        {activeView === "settings" && renderSettings()}
        {activeView === "aromaDilution" && renderAromaDilution()}
{activeView === "coilCalculator" && (
          <CoilCalculator getStyles={getStyles} theme={theme} t={t} />
        )}
        {activeView === "ohmCalculator" && (
          <OhmCalculator getStyles={getStyles} theme={theme} t={t} />
        )}
        {activeView === "recipes" && renderRecipes()}
        {activeView === "vapingEducativo" && (
          <VapingEducativo getStyles={getStyles} theme={theme} />
        )}
        {activeView === "privacyPolicy" && (
          <PrivacyPolicy getStyles={getStyles} theme={theme} />
        )}
      </>

      {showAromaModal && (
        <div style={getStyles(theme).modal}>
          <div style={getStyles(theme).modalContent}>
            <h3 style={getStyles(theme).modalTitle}>{t("addAroma")}</h3>
            <div style={getStyles(theme).inputGroup}>
              <label style={getStyles(theme).label}>
                {t("baseAndPercentage")}
              </label>
              <div style={getStyles(theme).inputWrapper}>
                <input
                  style={getStyles(theme).input}
                  type="number"
                  value={newAromaPercentage}
                  onChange={(e) => setNewAromaPercentage(e.target.value)}
                  placeholder="Percentuale"
                />
                <span style={getStyles(theme).unit}>%</span>
              </div>
            </div>
            <div style={getStyles(theme).aromaComposition}>
              <button
                style={{
                  ...getStyles(theme).compositionButton,
                  ...(aromaVgPg === "PG" ? getStyles(theme).activeButton : {}),
                }}
                onClick={() => setAromaVgPg("PG")}
              >
                PG
              </button>
              <button
                style={{
                  ...getStyles(theme).compositionButton,
                  ...(aromaVgPg === "VG" ? getStyles(theme).activeButton : {}),
                }}
                onClick={() => setAromaVgPg("VG")}
              >
                VG
              </button>
            </div>
            <div style={getStyles(theme).modalButtons}>
              <button
                style={getStyles(theme).cancelButton}
                onClick={() => setShowAromaModal(false)}
              >
                {t("cancel")}
              </button>
              <button style={getStyles(theme).saveButton} onClick={addAroma}>
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default McPuff;
